/*
  The room conversation, inside the library card.

  Sized to sit in a card rather than fill a page: the library list is the
  screen, and the chat is one part of it. A full-height chat would push the
  Leave button off a phone.
*/
/*
  *** A PANEL, NOT A BARE SECTION ***
  Edge to edge with only a hairline above it, the chat read as loose text
  on the page rather than a place to talk. A surface and a border give it
  an edge, which is what makes it look like somewhere messages live.
*/
.room {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-4);
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
}

.title {
  margin: 0;
  font-size: var(--text-xs);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-subtle);
}

/*
  A fixed height, not a growing one: the card must stay the same size as
  messages arrive, or the Leave button moves while someone is reaching for
  it. Scrolls internally instead.
*/
.log {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  /* Grows with the room rather than a fixed box: on a full screen there is
     space for the conversation, and min-height keeps it usable when there
     are only two messages. */
  min-height: 10rem;
  max-height: 20rem;
  overflow-y: auto;
  padding: var(--space-3);
  background: var(--bg-sunken);
  border-radius: var(--radius);
}

.line {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  align-items: flex-start;
  max-width: 85%;
}

/* Own messages to the right, the way every chat reads. */
.mine {
  align-self: flex-end;
  align-items: flex-end;
}

.sender {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--text-muted);
}

.badge {
  background: var(--indigo-soft);
  color: var(--indigo);
  border-radius: var(--radius-full);
  padding: 0 0.375rem;
  font-size: var(--text-xs);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.bubble {
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: var(--space-2) var(--space-3);
  display: flex;
  align-items: baseline;
  gap: var(--space-2);
  /* A pasted URL or a long word must wrap rather than widen the card. */
  overflow-wrap: anywhere;
}

/* The one answer worth finding in a room of twenty. */
.bubbleMentor {
  border-color: var(--indigo);
  background: var(--indigo-soft);
}

.body {
  font-size: var(--text-base);
  color: var(--text);
  /* Keeps the line breaks the sender typed. */
  white-space: pre-wrap;
}

.time {
  font-size: var(--text-xs);
  color: var(--text-muted);
  white-space: nowrap;
  flex-shrink: 0;
}

.composer {
  display: flex;
  gap: var(--space-2);
  align-items: center;
}

.input {
  flex: 1;
  min-width: 0;
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: var(--space-2) var(--space-3);
  font: inherit;
  font-size: var(--text-base);
  color: var(--text);
}

.input:focus {
  outline: none;
  border-color: var(--indigo);
  box-shadow: 0 0 0 3px var(--indigo-soft);
}

.input::placeholder {
  color: var(--text-subtle);
}

.muted {
  margin: 0;
  font-size: var(--text-sm);
  color: var(--text-muted);
}

.error {
  margin: 0;
  font-size: var(--text-sm);
  color: var(--danger);
}

/* ------------------------------------------------- the chat's header */
.head {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.controls {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 2px;
}

/*
  *** AN ICON BUTTON ALWAYS CARRIES A TOOLTIP ***
  An icon alone is a guess until you click it. Drawn by the page rather
  than left to the browser's `title`, which waits about a second and then
  appears wherever the cursor happens to be rather than beside the control.
*/
.iconBtn {
  position: relative;
  width: 1.75rem;
  height: 1.75rem;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: 1px solid transparent;
  border-radius: var(--radius-sm);
  font: inherit;
  font-size: var(--text-md);
  line-height: 1;
  color: var(--text-muted);
  cursor: pointer;
}

.iconBtn:hover,
.iconBtn:focus-visible {
  background: var(--bg-sunken);
  border-color: var(--border);
  color: var(--text);
}

.iconBtn::after {
  content: attr(data-tip);
  position: absolute;
  bottom: calc(100% + 0.4rem);
  right: 0;
  white-space: nowrap;
  padding: 0.3rem 0.5rem;
  border-radius: var(--radius-sm);
  background: var(--text);
  color: var(--bg-elevated);
  font-size: var(--text-xs);
  font-weight: 500;
  letter-spacing: 0;
  text-transform: none;
  opacity: 0;
  pointer-events: none;
  transition: opacity 120ms ease;
  z-index: 30;
}

.iconBtn:hover::after,
.iconBtn:focus-visible::after {
  opacity: 1;
}

@media (prefers-reduced-motion: reduce) {
  .iconBtn::after { transition: none; }
}

@media (max-width: 30rem) {
  /* Anchored right, a tooltip runs off a narrow screen. */
  .iconBtn::after {
    right: auto;
    left: 50%;
    transform: translateX(-50%);
  }
}
